import { useState, useCallback, useEffect } from "react";
import type { Minister } from "@/models/minister";
import type { MinisterFormData, MinisterFormErrors } from "../types";
import {
  validateMinisterForm,
  checkAdbCodeUniqueness,
  checkCIUniqueness,
  generateFullName,
  normalizeDNI,
} from "../utils/validations";
import { processImage, constructPhotoUrl } from "../utils/imageProcessing";

/**
 * Hook to manage minister form state, validation, and photo upload
 */
export function useMinisterForm(existingMinister?: Minister) {
  // Form data state
  const [formData, setFormData] = useState<MinisterFormData>(() => ({
    adb_code: existingMinister?.adb_code || "",
    first_name: existingMinister?.first_name || "",
    last_name: existingMinister?.last_name || "",
    second_last_name: existingMinister?.second_last_name || "",
    marriage_last_name: existingMinister?.marriage_last_name || "",
    dni: existingMinister?.dni || "",
    degree_id: existingMinister?.degree_id || "",
    district_id: existingMinister?.district_id || "",
    region_id: existingMinister?.region_id || "",
    position: existingMinister?.position || "",
    email: existingMinister?.email || "",
    cell_phone: existingMinister?.cell_phone || "",
    office_phone: existingMinister?.office_phone || "",
    address: existingMinister?.address || "",
    address_city: existingMinister?.address_city || "",
    address_zone: existingMinister?.address_zone || "",
    photo_url: "", // Photo URL will be handled separately
    is_active: existingMinister?.is_active ?? true, // Default to active for new ministers
  }));

  // Validation errors state
  const [errors, setErrors] = useState<MinisterFormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof MinisterFormData, boolean>>>({});

  // Photo upload state
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [processedPhotoFile, setProcessedPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const [shouldDeletePhoto, setShouldDeletePhoto] = useState(false);

  /**
   * Update a single form field with real-time validation
   */
  const updateField = useCallback(
    <K extends keyof MinisterFormData>(field: K, value: MinisterFormData[K]) => {
      let normalizedValue = value;
      
      // Auto-normalize DNI format (uppercase letters after space)
      if (field === 'dni' && typeof value === 'string') {
        normalizedValue = normalizeDNI(value) as MinisterFormData[K];
      }
      
      setFormData(prev => ({ ...prev, [field]: normalizedValue }));
      setTouched(prev => ({ ...prev, [field]: true }));
      
      // Clear error for this field if it now has a value (for required fields)
      if (normalizedValue && typeof normalizedValue === 'string' && normalizedValue.trim()) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field as keyof MinisterFormErrors];
          return newErrors;
        });
      }
    },
    []
  );

  /**
   * Mark a field as touched
   */
  const markFieldTouched = useCallback((field: keyof MinisterFormData) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  /**
   * Validate the form
   */
  const validate = useCallback(async (): Promise<boolean> => {
    // Basic validation
    const basicValidation = validateMinisterForm({
      adb_code: formData.adb_code,
      first_name: formData.first_name,
      last_name: formData.last_name,
      dni: formData.dni,
      degree_id: formData.degree_id,
      district_id: formData.district_id,
      region_id: formData.region_id,
      email: formData.email,
    }, { skipEcclesialRequired: !!existingMinister });

    const newErrors: MinisterFormErrors = { ...basicValidation.errors };

    // Check ADB code uniqueness
    const adbCheck = await checkAdbCodeUniqueness(
      formData.adb_code,
      existingMinister?.id
    );
    if (!adbCheck.unique) {
      newErrors.adb_code = adbCheck.message!;
    }

    // Check CI uniqueness
    const ciCheck = await checkCIUniqueness(formData.dni, existingMinister?.id);
    if (!ciCheck.unique) {
      newErrors.dni = ciCheck.message!;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, existingMinister?.id]);

  // Initialize photo preview from existing minister and run validation
  useEffect(() => {
    if (existingMinister?.id) {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const photoUrl = constructPhotoUrl(supabaseUrl, existingMinister.profile_picture_filename || null);
      setPhotoPreview(photoUrl);
      
      // Run validation on load for edit mode
      validate();
    }
    // Note: validate is wrapped in useCallback with stable dependencies
    // We only want to run this once when minister loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingMinister?.id, existingMinister?.profile_picture_filename]);

  /**
   * Handle photo file selection - processes and compresses the image
   */
  const handlePhotoSelect = useCallback(async (file: File | null) => {
    setPhotoFile(file);
    setPhotoUploadError(null);

    if (file) {
      try {
        // Process image (resize/compress for JPEG)
        setIsUploadingPhoto(true);
        const processed = await processImage(file);
        setProcessedPhotoFile(processed);
        setShouldDeletePhoto(false); // Clear delete flag when uploading new photo
        
        // Create preview from processed file
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreview(reader.result as string);
          setIsUploadingPhoto(false);
        };
        reader.readAsDataURL(processed);
      } catch (error) {
        console.error('Error processing image:', error);
        setPhotoUploadError('Error al procesar la imagen');
        setIsUploadingPhoto(false);
        setProcessedPhotoFile(null);
        setPhotoPreview(null);
      }
    } else {
      setProcessedPhotoFile(null);
      setPhotoPreview(null);
      setPhotoUploadError(null);
      // If we're editing and clearing photo, mark for deletion
      if (existingMinister?.profile_picture_filename) {
        setShouldDeletePhoto(true);
      }
    }
  }, [existingMinister?.profile_picture_filename]);



  /**
   * Generate full name preview
   */
  const fullNamePreview = generateFullName(
    formData.first_name,
    formData.last_name,
    formData.second_last_name,
    formData.marriage_last_name
  );

  /**
   * Reset form to initial state
   */
  const reset = useCallback(() => {
    setFormData({
      adb_code: existingMinister?.adb_code || "",
      first_name: existingMinister?.first_name || "",
      last_name: existingMinister?.last_name || "",
      second_last_name: existingMinister?.second_last_name || "",
      marriage_last_name: existingMinister?.marriage_last_name || "",
      dni: existingMinister?.dni || "",
      degree_id: existingMinister?.degree_id || "",
      district_id: existingMinister?.district_id || "",
      region_id: existingMinister?.region_id || "",
      position: existingMinister?.position || "",
      email: existingMinister?.email || "",
      cell_phone: existingMinister?.cell_phone || "",
      office_phone: existingMinister?.office_phone || "",
      address: existingMinister?.address || "",
      address_city: existingMinister?.address_city || "",
      address_zone: existingMinister?.address_zone || "",
      photo_url: "",
      is_active: existingMinister?.is_active ?? true,
    });
    setErrors({});
    setTouched({});
    setPhotoFile(null);
    setProcessedPhotoFile(null);
    setPhotoPreview(null);
    setPhotoUploadError(null);
    setShouldDeletePhoto(false);
  }, [existingMinister]);

  return {
    formData,
    errors,
    touched,
    updateField,
    markFieldTouched,
    validate,
    photoFile,
    processedPhotoFile,
    photoPreview,
    handlePhotoSelect,
    isUploadingPhoto,
    photoUploadError,
    fullNamePreview,
    shouldDeletePhoto,
    reset,
  };
}
